import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, scaricaFile } from "@/lib/api";
import { euro, num, dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, PackagePlus, Pencil, FileSpreadsheet } from "lucide-react";
import { CaricoDialog, caricoInForm } from "./CaricoDialog";
import { PesoBadge } from "./PesoBadge";
import { useAuth } from "@/context/AuthContext";
import { FiltriMovimenti, FILTRI_MOVIMENTI_VUOTI, filtraMovimenti, filtriAttivi } from "./FiltriPrelievi";

export function CarichiTab() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null);
  const [filtri, setFiltri] = useState(FILTRI_MOVIMENTI_VUOTI);
  const { data: carichi = [] } = useQuery({ queryKey: ["mag-carichi"], queryFn: async () => (await api.get("/magazzino/carichi")).data });
  const { data: commesse = [] } = useQuery({ queryKey: ["commesse"], queryFn: async () => (await api.get("/commesse")).data });
  const filtrati = filtraMovimenti(carichi, filtri, "fornitore_id");
  const attivi = filtriAttivi(filtri);
  const soloArticolo = (c) => (filtri.articolo_id === "tutti" ? c.righe : c.righe.filter((r) => r.articolo_id === filtri.articolo_id));
  const totale = filtrati.reduce((s, c) => s + soloArticolo(c).reduce((t, r) => t + (r.importo || 0), 0), 0);
  const exportExcel = () => {
    const q = new URLSearchParams();
    [["soggetto_id", "fornitore_id"], ["articolo_id", "articolo_id"], ["commessa_id", "commessa_id"]].forEach(([k, p]) => filtri[k] !== "tutti" && q.set(p, filtri[k]));
    if (filtri.dal) q.set("dal", filtri.dal);
    if (filtri.al) q.set("al", filtri.al);
    scaricaFile(`/magazzino/carichi/export/excel?${q}`, (m) => toast.error(m));
  };
  const onError = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/magazzino/carichi/${id}`)).data,
    onSuccess: () => { toast.success("Documento spostato nel cestino"); ["mag-carichi", "mag-giacenze", "commessa", "materiali", "commesse"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError,
  });
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap border-b border-slate-100">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm text-slate-500" data-testid="carichi-conteggio">{attivi ? `${filtrati.length} di ${carichi.length} documenti` : `${carichi.length} documenti`} · <b>DDT</b> e <b>fattura</b> caricano entrambi la merce in giacenza.</div>
          <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm" data-testid="carichi-totale-box">
            <span className="text-slate-600 mr-2">{attivi ? "Totale filtrato" : "Totale carichi"}</span><b className="font-mono text-primary" data-testid="carichi-totale">{euro(totale)}</b>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportExcel} data-testid="carichi-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel{attivi ? " filtrato" : ""}</Button>
          <Button onClick={() => setDialog({})} data-testid="carico-nuovo"><PackagePlus className="h-4 w-4 mr-1.5" /> Nuovo carico</Button>
        </div>
      </div>
      <FiltriMovimenti f={filtri} onChange={setFiltri} soggetto="fornitore" commesse={commesse} testid="carichi" />
      <div className="divide-y divide-slate-100">
        {filtrati.map((c) => (
          <div key={c.id} className="p-4" data-testid={`carico-${c.ddt}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-sm">{dateIt(c.data)}</span>
              <span className="font-semibold text-slate-800">{c.tipo_documento === "fattura" ? "Fattura" : "DDT"} {c.ddt}</span>
              <span className="text-slate-500 text-sm">{c.fornitore_nome || "—"}</span>
              {c.parziale && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`carico-parziale-${c.ddt}`}>Consegna parziale</Badge>}
              {c.commessa_codice && <Badge className="bg-orange-100 text-primary hover:bg-orange-100" title={c.commessa_descrizione} data-testid={`carico-commessa-${c.ddt}`}>Commessa {c.commessa_codice}</Badge>}
              <span className="ml-auto font-mono font-semibold">{euro(c.totale)}</span>
              <PesoBadge peso={c.peso_totale} parziale={c.peso_parziale} testid={`carico-peso-${c.ddt}`} />
              <button onClick={() => setDialog({ editId: c.id, iniziale: caricoInForm(c) })} className="text-slate-400 hover:text-primary" title="Modifica" data-testid={`carico-edit-${c.ddt}`}><Pencil className="h-4 w-4" /></button>
              {isAdmin && <button onClick={() => window.confirm(`Eliminare ${c.tipo_documento === "fattura" ? "la fattura" : "il carico DDT"} ${c.ddt}?`) && delMut.mutate(c.id)} className="text-slate-300 hover:text-red-600" data-testid={`carico-del-${c.ddt}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
            <div className="mt-2 text-xs text-slate-600 grid sm:grid-cols-2 gap-x-6 gap-y-1">
              {c.righe.map((r, i) => (
                <div key={i} className="flex justify-between gap-2"><span><span className="font-mono">{r.codice}</span> {r.descrizione}{r.variante ? ` - ${r.variante}` : ""}</span><span className="font-mono">{num(r.quantita, 2)}{r.quantita_ordinata ? `/${num(r.quantita_ordinata, 2)}` : ""} {r.um} × {euro(r.costo_unitario)}</span></div>
              ))}
            </div>
            {c.note && <div className="mt-1 text-xs text-slate-400 italic">{c.note}</div>}
          </div>
        ))}
        {filtrati.length === 0 && <div className="p-6 text-center text-slate-400 text-sm" data-testid="carichi-vuoto">{carichi.length ? "Nessun documento corrisponde ai filtri." : "Nessun carico registrato."}</div>}
      </div>
      {dialog && <CaricoDialog open onClose={() => setDialog(null)} editId={dialog.editId} iniziale={dialog.iniziale} />}
    </div>
  );
}
